import { useEffect, useMemo } from 'react';

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { TBatchFindingsResponse } from '@/modules/insuranceExport/types';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';

export interface BatchFindingForVoiceNotes {
  unifiedAppointmentId: number;
  imagingPatientId: string;
}

export const useTreatmentsBatch = (
  inputAppointments?: BatchFindingForVoiceNotes[],
) => {
  const queryClient = useQueryClient();

  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  // Split into batches of 10
  const groupedAppointments = useMemo(() => {
    const groups: BatchFindingForVoiceNotes[][] = [];

    if (!inputAppointments?.length) return groups;

    for (let i = 0; i < inputAppointments.length; i += 10) {
      groups.push(inputAppointments.slice(i, i + 10));
    }

    return groups;
  }, [inputAppointments]);

  const queries = useMemo(() => {
    return groupedAppointments.map((batch, index) => ({
      queryKey: [
        endpoints.claimBatchFindings,
        'batch',
        index,
        batch.length,
        batch[0]?.unifiedAppointmentId,
      ],
      queryFn: async () => {
        const resp = await makeAPIRequestReactQuery<TBatchFindingsResponse[]>(
          endpoints.claimBatchFindings,
          undefined,
          'post',
          {
            headers: {
              Practice: currentPractice,
              Org: currentOrg,
            },
            data: {
              appointments: batch,
            },
          },
        );

        // Flatten response
        const responseArray = Array.isArray(resp) ? resp : [resp];

        return responseArray.flatMap((batchResponse) =>
          batchResponse.appointmentData.filter(Boolean),
        );
      },
      enabled: Boolean(batch.length && currentPractice && currentOrg),
      staleTime: 10 * 1000,
    }));
  }, [groupedAppointments, currentPractice, currentOrg]);

  // Clean cache on unmount like the old hook
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: [endpoints.claimBatchFindings],
      });
    };
  }, [queryClient]);

  return useQueries({ queries });
};
